import { useMemo, useState } from "react";

import { format } from "date-fns";

import { Namespace, useDateFnsLocale, useT } from "@racoon/i18n";

import {
  useAnomalieRouteQuery,
  useGetGovTruckTravelPathQuery,
} from "~/shared/api/govDash/govDash.api";
import { CardContentWrapper } from "~/ui/components/cardContent.wrapper";
import { InfoRow } from "~/ui/components/infoRow";

import { ViewSelector } from "../../../shared/components/viewSelector";
import { AnomaliesMap } from "./anomalies-map";

interface AnomaliesDetailsProps {
  selectedRecord: any;
}

export function AnomaliesDetails({ selectedRecord }: AnomaliesDetailsProps) {
  const t = useT();
  const dateFnsLocale = useDateFnsLocale();
  const [activeSection, setActiveSection] = useState<"general" | "map">("general");

  const { data: anomalieRoute } = useAnomalieRouteQuery({
    id: BigInt(selectedRecord?.id || 0),
  });

  const { data: travelPath } = useGetGovTruckTravelPathQuery(
    {
      truckId: selectedRecord?.truck?.id || 0,
      fromTo: {
        from: selectedRecord?.gpsStartAt
          ? { seconds: BigInt(Math.floor(Number(selectedRecord.gpsStartAt.seconds) - 1800)) }
          : undefined,
        to: selectedRecord?.gpsEndAt
          ? { seconds: BigInt(Math.floor(Number(selectedRecord.gpsEndAt.seconds) + 1800)) }
          : undefined,
      },
    },
    { skip: !anomalieRoute },
  );

  const viewOptions = useMemo(
    () =>
      [
        { value: "general" as const, label: t(Namespace.Govdash, "general") },
        // { value: "summary" as const, label: t(Namespace.Govdash, "summary") },
        { value: "map" as const, label: t(Namespace.Govdash, "map") },
      ] as const,
    [t],
  );

  return (
    <CardContentWrapper className="h-full flex flex-col">
      <div className="p-2">
        <ViewSelector
          options={viewOptions}
          activeSection={activeSection}
          setActiveSection={setActiveSection}
        />
      </div>

      {activeSection === "general" && (
        <div className="space-y-4 p-4">
          <CardContentWrapper>
            <InfoRow
              label={t(Namespace.Govdash, "anomalies.type")}
              value={t(Namespace.Govdash, "anomalies.unauthorizedStop")}
            />
            <InfoRow
              label={t(Namespace.Govdash, "plateNumber")}
              value={selectedRecord?.truck?.plateNumber || "N/A"}
            />
            <InfoRow
              label={t(Namespace.Govdash, "vehicleModel")}
              value={selectedRecord?.truck?.model || "N/A"}
            />
            <InfoRow
              label={t(Namespace.Govdash, "serviceProvider")}
              value={selectedRecord?.ownerOrganization?.name || "N/A"}
            />
            <InfoRow
              label={t(Namespace.Govdash, "anomalies.time")}
              value={
                selectedRecord?.gpsStartAt?.seconds
                  ? format(
                      new Date(Number(selectedRecord.gpsStartAt.seconds) * 1000),
                      "HH:mm dd MMM yyyy",
                      { locale: dateFnsLocale },
                    )
                  : "N/A"
              }
            />
          </CardContentWrapper>
        </div>
      )}

      {activeSection === "map" && (
        <div className="flex-1 relative min-h-0 p-2">
          <AnomaliesMap anomaly={selectedRecord} travelPath={travelPath} />
        </div>
      )}
    </CardContentWrapper>
  );
}
